// ── SMALL SOUNDS ─────────────────────────────────────────────────────────────
//
// Footsteps, a landing thud, UI blips — synthesised, so there is nothing to
// download and nothing to license. Quiet by design: the radio is the
// soundtrack, these are the room tone that says you have a body.
//
// iOS rule (reference_ios_voice_connect_hang): the AudioContext is created
// INSIDE a user gesture and never awaited. unlock() is wired to the first
// pointer/key event; until then every play call is a silent no-op.

let ctx = null;
let master = null;
let noiseBuf = null;

function makeNoise(c) {
  const len = Math.floor(c.sampleRate * 0.25);
  const b = c.createBuffer(1, len, c.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return b;
}

export function unlockSfx() {
  if (ctx) return;
  const C = window.AudioContext || window.webkitAudioContext;
  if (!C) return;
  try {
    ctx = new C();
    master = ctx.createGain();
    master.gain.value = 0.5;
    master.connect(ctx.destination);
    noiseBuf = makeNoise(ctx);
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  } catch { ctx = null; }
}

if (typeof window !== 'undefined') {
  const once = () => { unlockSfx(); window.removeEventListener('pointerdown', once); window.removeEventListener('keydown', once); };
  window.addEventListener('pointerdown', once);
  window.addEventListener('keydown', once);
}

export function setSfxVolume(v) { if (master) master.gain.value = Math.max(0, Math.min(1, v)); }

/** One footstep. `run` makes it harder and brighter; `surface` 0 asphalt … 1 hard pavement. */
export function footstep({ run = false, surface = 0.5, gain = 1 } = {}) {
  if (!ctx || ctx.state !== 'running') return;
  const t = ctx.currentTime;
  const src = ctx.createBufferSource();
  src.buffer = noiseBuf;
  src.playbackRate.value = 0.8 + Math.random() * 0.4;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = (run ? 900 : 650) + surface * 500 + Math.random() * 150;
  bp.Q.value = 0.9;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2600 + surface * 1500;
  const g = ctx.createGain();
  const peak = (run ? 0.16 : 0.09) * gain;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t + (run ? 0.09 : 0.12));
  src.connect(bp).connect(lp).connect(g).connect(master);
  src.start(t);
  src.stop(t + 0.15);
}

/** Landing from a jump. */
export function thud(gain = 1) {
  if (!ctx || ctx.state !== 'running') return;
  const t = ctx.currentTime;
  const o = ctx.createOscillator();
  o.type = 'sine';
  o.frequency.setValueAtTime(120, t);
  o.frequency.exponentialRampToValueAtTime(45, t + 0.12);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.18 * gain, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
  o.connect(g).connect(master);
  o.start(t); o.stop(t + 0.2);
  footstep({ run: true, surface: 0.7, gain: 1.2 * gain });
}

/** A soft UI blip (zone cards, radio switch). */
export function blip(freq = 880, gain = 1) {
  if (!ctx || ctx.state !== 'running') return;
  const t = ctx.currentTime;
  const o = ctx.createOscillator();
  o.type = 'triangle';
  o.frequency.setValueAtTime(freq, t);
  o.frequency.exponentialRampToValueAtTime(freq * 1.5, t + 0.08);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.05 * gain, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
  o.connect(g).connect(master);
  o.start(t); o.stop(t + 0.18);
}

/** A whoosh (sprint start, car door) — filtered noise sweep. */
export function whoosh(gain = 1) {
  if (!ctx || ctx.state !== 'running') return;
  const t = ctx.currentTime;
  const src = ctx.createBufferSource();
  src.buffer = noiseBuf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass'; bp.Q.value = 1.2;
  bp.frequency.setValueAtTime(400, t);
  bp.frequency.exponentialRampToValueAtTime(1800, t + 0.22);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.06 * gain, t + 0.05);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
  src.connect(bp).connect(g).connect(master);
  src.start(t); src.stop(t + 0.26);
}

export function sfxContext() { return ctx; }

// ── THE ENGINE ───────────────────────────────────────────────────────────────
// Two detuned oscillators through a low-pass that opens with the revs, plus a
// band of noise for the tyres when the car is sliding. Quiet under the radio —
// you should feel the car more than hear it.
let engine = null;
export function engineStart() {
  if (!ctx || engine) return;
  const t = ctx.currentTime;
  const out = ctx.createGain(); out.gain.value = 0.0001; out.connect(master);
  const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 500; lp.Q.value = 2;
  lp.connect(out);
  const a = ctx.createOscillator(); a.type = 'sawtooth'; a.frequency.value = 42;
  const b = ctx.createOscillator(); b.type = 'square'; b.frequency.value = 42 * 1.503;
  const ga = ctx.createGain(); ga.gain.value = 0.55; const gb = ctx.createGain(); gb.gain.value = 0.25;
  a.connect(ga).connect(lp); b.connect(gb).connect(lp);
  const tyre = ctx.createBufferSource(); tyre.buffer = noiseBuf; tyre.loop = true;
  const tbp = ctx.createBiquadFilter(); tbp.type = 'bandpass'; tbp.frequency.value = 1400; tbp.Q.value = 3;
  const tg = ctx.createGain(); tg.gain.value = 0.0001;
  tyre.connect(tbp).connect(tg).connect(master);
  a.start(t); b.start(t); tyre.start(t);
  out.gain.exponentialRampToValueAtTime(0.09, t + 0.4);
  engine = { a, b, lp, out, tyre, tg };
}
/** rpm 0..1, load 0..1 (throttle), slide 0..1 (tyre squeal). */
export function engineSet(rpm, load, slide = 0) {
  if (!engine || !ctx) return;
  const t = ctx.currentTime;
  const f = 38 + rpm * 120;
  engine.a.frequency.setTargetAtTime(f, t, 0.05);
  engine.b.frequency.setTargetAtTime(f * 1.503, t, 0.05);
  engine.lp.frequency.setTargetAtTime(380 + rpm * 1500 + load * 600, t, 0.08);
  engine.out.gain.setTargetAtTime(0.05 + load * 0.06 + rpm * 0.03, t, 0.1);
  engine.tg.gain.setTargetAtTime(Math.max(0.0001, slide * 0.08), t, 0.06);
}
export function engineStop() {
  if (!engine || !ctx) return;
  const t = ctx.currentTime;
  const e = engine; engine = null;
  e.out.gain.setTargetAtTime(0.0001, t, 0.15);
  e.tg.gain.setTargetAtTime(0.0001, t, 0.05);
  setTimeout(() => { try { e.a.stop(); e.b.stop(); e.tyre.stop(); } catch { /* noop */ } }, 700);
}

/** A crash: a low thump and a burst of scrape. */
export function crash(gain = 1) {
  if (!ctx || ctx.state !== 'running') return;
  thud(Math.min(1, gain));
  const t = ctx.currentTime;
  const src = ctx.createBufferSource(); src.buffer = noiseBuf;
  const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 900;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.12 * gain, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
  src.connect(hp).connect(g).connect(master);
  src.start(t); src.stop(t + 0.32);
}

/** A car door. */
export function door() {
  if (!ctx || ctx.state !== 'running') return;
  const t = ctx.currentTime;
  const o = ctx.createOscillator(); o.type = 'square';
  o.frequency.setValueAtTime(90, t); o.frequency.exponentialRampToValueAtTime(55, t + 0.08);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.1, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
  const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 600;
  o.connect(lp).connect(g).connect(master);
  o.start(t); o.stop(t + 0.14);
}

// ── RAIN AND THUNDER ─────────────────────────────────────────────────────────
// A bed of filtered noise whose level follows the weather — the hiss on the
// street and the patter close by — and a long low rumble after lightning.
let rain = null;
export function rainSet(level) {
  if (!ctx) return;
  if (!rain && level > 0.01) {
    const t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = noiseBuf; src.loop = true;
    const hiss = ctx.createBiquadFilter(); hiss.type = 'bandpass'; hiss.frequency.value = 2400; hiss.Q.value = 0.5;
    const low = ctx.createBiquadFilter(); low.type = 'lowpass'; low.frequency.value = 900;
    const gh = ctx.createGain(); gh.gain.value = 0.0001;
    const gl = ctx.createGain(); gl.gain.value = 0.0001;
    src.connect(hiss).connect(gh).connect(master);
    src.connect(low).connect(gl).connect(master);
    src.start(t);
    rain = { src, gh, gl };
  }
  if (!rain) return;
  const t = ctx.currentTime;
  rain.gh.gain.setTargetAtTime(Math.max(0.0001, level * 0.07), t, 0.5);
  rain.gl.gain.setTargetAtTime(Math.max(0.0001, level * 0.05), t, 0.5);
}

export function thunder(gain = 1) {
  if (!ctx || ctx.state !== 'running') return;
  const t = ctx.currentTime;
  const src = ctx.createBufferSource(); src.buffer = noiseBuf; src.loop = true;
  const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.setValueAtTime(260, t);
  lp.frequency.exponentialRampToValueAtTime(70, t + 3.5);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.32 * gain, t + 0.08);
  g.gain.setValueAtTime(0.32 * gain, t + 0.4);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 4);
  src.connect(lp).connect(g).connect(master);
  src.start(t); src.stop(t + 4.1);
}
